import { useEffect, useRef } from 'react';

/**
 * 리스트에서 카드 클릭 → 상세 진입 → 뒤로가기 시,
 * 마지막으로 클릭한 카드 위치로 스크롤을 복원한다.
 *
 * - 클릭 시 `saveCardId(questionId)`로 카드 ID를 sessionStorage에 저장
 * - 리스트 마운트 시 저장된 카드(`#q-{id}`)를 화면 상단으로 정렬
 * - 앵커 카드가 아직 로드되지 않았으면(무한스크롤 캐시 만료 등)
 *   추가 페이지를 로드하며 나타날 때까지 재시도
 * - 이미지/광고 슬롯 지연 로딩으로 위치가 밀리는 것을 짧게 재보정
 *
 * 각 카드 래퍼에는 반드시 `id={`q-${questionId}`}`를 부여해야 한다.
 */
interface UseCardScrollRestoreParams {
  /** 현재 렌더된 카드 수 (데이터 로드 진행에 따라 변함) */
  itemCount: number;
  /** sessionStorage 키 (화면별로 분리) */
  storageKey: string;
  /** 무한스크롤일 때만 전달 */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

/** 앵커 카드 탐색을 위한 최대 추가 페이지 로드 횟수 */
const MAX_FETCH_ATTEMPTS = 10;
/** 이미지/광고 지연 로딩 후 위치 재보정 시점(ms) */
const REALIGN_DELAYS_MS = [120, 360];

export const useCardScrollRestore = ({
  itemCount,
  storageKey,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
}: UseCardScrollRestoreParams) => {
  const restored = useRef(false);
  const fetchAttempts = useRef(0);

  useEffect(() => {
    if (restored.current) return;

    const savedId = sessionStorage.getItem(storageKey);
    if (!savedId) {
      restored.current = true;
      return;
    }
    // 데이터가 아직 없으면 다음 렌더를 기다림
    if (itemCount === 0) return;

    const target = document.getElementById(`q-${savedId}`);
    if (target) {
      restored.current = true;
      sessionStorage.removeItem(storageKey);
      const align = () => document.getElementById(`q-${savedId}`)?.scrollIntoView({ block: 'start' });
      const raf = requestAnimationFrame(align);
      // 이미지/광고 슬롯이 나중에 로드되며 위치가 밀리는 것을 보정
      const timers = REALIGN_DELAYS_MS.map((delay) => window.setTimeout(align, delay));
      return () => {
        cancelAnimationFrame(raf);
        timers.forEach(clearTimeout);
      };
    }

    // 앵커 카드가 아직 로드되지 않음 → 추가 페이지를 로드하며 재시도
    if (fetchNextPage && hasNextPage && !isFetchingNextPage && fetchAttempts.current < MAX_FETCH_ATTEMPTS) {
      fetchAttempts.current += 1;
      fetchNextPage();
      return;
    }

    // 더 로드할 페이지가 없는데도 카드를 못 찾음(삭제/필터 변경 등) → 복원 포기
    if (!hasNextPage) {
      restored.current = true;
      sessionStorage.removeItem(storageKey);
    }
  }, [itemCount, storageKey, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const saveCardId = (id: string | number) => {
    sessionStorage.setItem(storageKey, String(id));
  };

  return { saveCardId };
};
